/**
 * video-keyframe-probe.js — extract keyframe (I-frame) timestamps from any
 * video file without decoding. Used by video-trim (and any tool that needs
 * frame-accurate cut points without re-encoding the entire file).
 *
 * Why this exists:
 *   FFmpeg's `-ss BEFORE -i` + `-c copy` SILENTLY SNAPS the start of a trim
 *   BACKWARDS to the nearest preceding keyframe. On long-GOP files (screen
 *   recordings, slideshow videos, low-motion content) keyframes can be 10+
 *   seconds apart — a user's 3-second start trim lands at 0 s and the file
 *   appears uncut. This is the root of feedback #718 ("не обрезает начало",
 *   2026-06-10 Aleksandr) and #640 ("вырезать оставляет", 2026-06-05 Eldar
 *   — same bug on the cut path: each kept-segment's start snaps backward
 *   into the cut region, leaving frames of the supposed-cut visible).
 *
 *   With the keyframe index we can: (a) snap UI handles to keyframes so the
 *   user sees what they'll get before saving; (b) auto-promote the save
 *   engine to a smart 2-pass trim (re-encode the head GOP for an exact
 *   start, copy the rest) only when the user's selStart is between
 *   keyframes; (c) apply the same per-segment fix to the cut path so cut
 *   boundaries are exact too.
 *
 * Tier ladder:
 *   1. mp4box.js stss + stts box parse — INSTANT (< 100 ms regardless of file
 *      length, verified on 0.5 s / 2 s / 10 s GOP fixtures, exact match vs
 *      ffprobe ground truth). Reads sync-sample box from container; no
 *      decoder, no WASM. Works for mp4/mov/m4v.
 *   2. FFmpeg WASM `-skip_frame nokey -vf showinfo` — UNIVERSAL FALLBACK,
 *      ~150 ms for a 20 s file, ~1-3 s for 30 min. Demuxer-level skip means
 *      we never spend cycles on P / B frames. Works for any container the
 *      FFmpeg build can demux (AVI, MKV, TS, FLV, OGV, WMV, plus mp4/mov).
 *
 * Result shape:
 *   {
 *     keyframes: [0, 2.0, 4.0, ...]   // sorted PRESENTATION times, seconds
 *     source:    'mp4box' | 'ffmpeg'  // which tier resolved this file
 *     duration:  47.2                  // total duration in seconds (from container)
 *     meanGopSec: 2.0                  // average gap between keyframes
 *     frames:    Float64Array         // mp4box tier: every frame's PTS, sorted
 *     splice:    {...}                 // mp4box tier: what window.__spliceKeyframeAfter reads
 *   }
 *   Only the mp4box tier can vouch for a splice (it knows where each
 *   keyframe's bytes are); the FFmpeg tier answers `splice` = undefined.
 *   Returns null on failure (corrupt moov, unsupported codec, FFmpeg not
 *   ready, etc.). Callers must handle null.
 *
 * Per-file caching: result is memoized on the File via a WeakMap so repeated
 * probes (e.g. on every save attempt) cost nothing.
 *
 * Helpers (alongside the probe):
 *   nearestKeyframeBefore(kfs, t)  — binary-search the largest kf <= t (or 0 if t < kfs[0])
 *   nearestKeyframeAfter(kfs, t)   — binary-search the smallest kf >= t (or last kf if t > last)
 *   nearestKeyframe(kfs, t)        — whichever of the two is closer to t
 *   isOnKeyframe(kfs, t, tolSec)   — true if |t − nearestKeyframe| <= tolSec (default 0.05 s)
 */
(function(){
'use strict';
if (window.__probeKeyframes) return;

var _cache = new WeakMap();

var MP4_FAMILY_RE = /\.(mp4|mov|m4v|m4a|3gp)$/i;
var MP4_MIME_RE   = /mp4|quicktime|3gpp/i;

/* ---- mp4box tier --------------------------------------------------------- */

/* Presentation-time index of the video track from mp4box's sample table:
   sorted PTS of every frame, the sync samples with the bytes a splice check
   needs, and the codec facts that decide whether a re-encoded head can sit in
   front of a copied tail at all. Null when the table is not available, and the
   caller falls back to the DTS walk (no splice detail — callers treat that as
   "cannot prove a splice", never as "fine"). */
function presentationIndex(mp4, trak, timescale) {
    try {
        if (typeof mp4.getTrackSamplesInfo !== 'function') return null;
        var samples = mp4.getTrackSamplesInfo(trak.tkhd.track_id);
        if (!samples || !samples.length) return null;
        /* Edit list: leading empty edits delay presentation, the first real
           edit's media_time is where presentation starts inside the media. A
           rate or a second real edit (slow-mo, iMovie joins) is not a plain
           shift — keep the times but refuse to vouch for a splice. */
        var movieTs = (mp4.moov && mp4.moov.mvhd && mp4.moov.mvhd.timescale) || timescale;
        var lead = 0, mediaTime = 0, realEdits = 0, oddRate = false;
        var ents = (trak.edts && trak.edts.elst && trak.edts.elst.entries) || [];
        for (var i = 0; i < ents.length; i++) {
            var en = ents[i];
            if (en.media_time === -1) { if (!realEdits) lead += en.segment_duration / movieTs; continue; }
            if (!realEdits) mediaTime = en.media_time;
            realEdits++;
            if (en.media_rate_integer !== undefined && en.media_rate_integer !== 1) oddRate = true;
        }
        /* Times are relative to the FILE's start — the earliest presentation
           of any track, never below 0 (audio priming hidden by an edit list
           starts "before" 0) — because that is what an ffmpeg `-ss` counts
           from (and Chrome's currentTime). Equal to 0 for ordinary files; an
           mp4 remuxed from TS can start at 1.4 s, and a splice point computed
           from 0 would land 1.4 s off. */
        var fileStart = Infinity;
        (mp4.moov.traks || []).forEach(function (tk) {
            try {
                var ss = tk === trak ? samples : mp4.getTrackSamplesInfo(tk.tkhd.track_id);
                if (!ss || !ss.length) return;
                var tts = tk.mdia.mdhd.timescale, tl = 0, tm = 0, got = false;
                ((tk.edts && tk.edts.elst && tk.edts.elst.entries) || []).forEach(function (en) {
                    if (got) return;
                    if (en.media_time === -1) tl += en.segment_duration / movieTs; else { tm = en.media_time; got = true; }
                });
                var first = Infinity;
                for (var q = 0; q < Math.min(ss.length, 8); q++) first = Math.min(first, tl + (ss[q].cts - tm) / tts);
                fileStart = Math.min(fileStart, Math.max(0, first));
            } catch (_) {}
        });
        if (!isFinite(fileStart)) fileStart = 0;
        var n = samples.length;
        var frames = new Float64Array(n);
        var syncs = [];
        for (var k = 0; k < n; k++) {
            var s = samples[k];
            var pts = lead + (s.cts - mediaTime) / timescale - fileStart;
            frames[k] = pts;
            if (s.is_sync) syncs.push({ t: pts, offset: s.offset, size: s.size, desc: s.description_index || 0 });
        }
        frames.sort();
        syncs.sort(function (a, b) { return a.t - b.t; });
        var stsd = trak.mdia.minf.stbl.stsd;
        var entry = (stsd && stsd.entries && stsd.entries[0]) || {};
        var avcC = entry.avcC || null;
        return {
            keyframes: syncs.map(function (x) { return x.t; }),
            frames: frames,
            splice: {
                syncs: syncs,
                sampleEntry: String(entry.type || ''),
                descCount: (stsd && stsd.entries) ? stsd.entries.length : 0,
                profileIdc: avcC ? avcC.AVCProfileIndication : 0,
                lengthSize: avcC ? (avcC.lengthSizeMinusOne & 3) + 1 : 0,
                editComplex: realEdits > 1 || oddRate,
                fileStart: fileStart,
            },
        };
    } catch (_) { return null; }
}

/* Can a re-encoded H.264 head be followed by a stream copy that starts at this
   keyframe, and still decode? Three things must hold, each one a class that
   shipped as a "successful" smart trim whose picture broke after the head:

   - The track is H.264 in 8-bit 4:2:0 (Baseline / Main / Extended / High).
     The head is ALWAYS libx264 yuv420p; an HEVC / VP9 / AV1 tail pasted after
     it is not a video anyone can decode (prod 30 d: 41 hevc, 14 vp9, 9 av1
     "smart_2pass_ok"), and a 10-bit or 4:2:2 tail changes the bit depth
     mid-stream.
   - ONE sample description. A second one (joined recordings) means the tail's
     parameter sets live in an entry the copy does not carry.
   - The keyframe is an IDR. A sync sample that is a recovery-point I-frame
     (open GOP) carries no reset: nothing re-sends its SPS/PPS, so the whole
     tail is decoded with the head's parameter sets, and its leading B-frames
     reference pictures that are not there. Measured on an x264 open-GOP
     source: 197 decodable frames out of 1004, Chrome `PIPELINE_ERROR_DECODE`,
     Media Foundation garbage — while the tool said "done".

   Reads the first VCL NAL of each candidate sample (one small slice), so the
   verdict is about the bytes, not about what the container claims. */
async function spliceKeyframeAfter(file, info, t, maxAheadSec) {
    var sp = info && info.splice;
    if (!sp) return { ok: false, reason: 'no_detail' };
    if (!/^avc[13]$/.test(sp.sampleEntry)) return { ok: false, reason: 'codec', entry: sp.sampleEntry };
    if ([66, 77, 88, 100].indexOf(sp.profileIdc) === -1) return { ok: false, reason: 'profile', profile: sp.profileIdc };
    if (sp.descCount !== 1) return { ok: false, reason: 'multi_stsd', count: sp.descCount };
    if (sp.editComplex) return { ok: false, reason: 'edit_list' };
    if (!sp.lengthSize) return { ok: false, reason: 'no_avcc' };
    var limit = t + (maxAheadSec || 45);
    var looked = 0, nonIdr = 0;
    for (var i = 0; i < sp.syncs.length; i++) {
        var s = sp.syncs[i];
        if (s.t < t - 1e-6) continue;
        if (s.t > limit) break;
        if (++looked > 12) break;
        var nal = await firstVclNalType(file, s.offset, s.size, sp.lengthSize);
        if (nal === 5) return { ok: true, t: s.t, skippedNonIdr: nonIdr };
        if (nal === -1) return { ok: false, reason: 'unreadable' };
        nonIdr++;
    }
    return { ok: false, reason: nonIdr ? 'no_idr' : 'no_keyframe', nonIdr: nonIdr };
}

async function firstVclNalType(file, offset, size, lengthSize) {
    try {
        if (!(offset >= 0) || !(size > 0)) return -1;
        var buf = new Uint8Array(await file.slice(offset, offset + Math.min(size, 65536)).arrayBuffer());
        var p = 0;
        while (p + lengthSize < buf.length) {
            var len = 0;
            for (var b = 0; b < lengthSize; b++) len = len * 256 + buf[p + b];
            var type = buf[p + lengthSize] & 0x1f;
            if (type >= 1 && type <= 5) return type;
            if (len <= 0) return -1;
            p += lengthSize + len;
        }
        return -1;
    } catch (_) { return -1; }
}

/* Parse the moov boxes mp4box exposes directly. mp4box has the stss
   (sync sample) box at trak.mdia.minf.stbl.stss with `sample_numbers`
   (1-based indices into the sample table) and stts (time-to-sample) at
   trak.mdia.minf.stbl.stts with parallel `sample_counts` + `sample_deltas`
   arrays. Walking stts gives a per-sample decoding-time; pulling out the
   times for stss's sample_numbers yields the keyframe times. Timescale on
   trak.mdia.mdhd converts to seconds. */
async function probeViaMp4Box(file) {
    if (!MP4_FAMILY_RE.test(file.name || '') && !MP4_MIME_RE.test(file.type || '')) return null;
    /* Load the library rather than skip the tier. Skipping used to cost only
       the FFmpeg tier's slower answer; now that tier cannot vouch for a splice
       (no sample table), so a save pressed before mp4box.js finished loading
       lost the fast path — and the WeakMap cache kept that answer for the
       file. Measured: the same trim went smart 2-pass on one run and full
       re-encode (`no_detail`) on the next. */
    if (!window.MP4Box && typeof window.__loadMp4Box === 'function') {
        try { await window.__loadMp4Box(); } catch (_) {}
    }
    if (typeof window.__Mp4BoxProbe !== 'function' || !window.MP4Box) return null;

    return await new Promise(function(resolve) {
        var mp4 = window.MP4Box.createFile();
        var settled = false;
        var timer = setTimeout(function() {
            if (settled) return; settled = true;
            try { mp4.flush(); } catch(_) {}
            resolve(null);
        }, 8000);

        mp4.onReady = function(info) {
            if (settled) return; settled = true; clearTimeout(timer);
            try {
                var trakVid = (mp4.moov && mp4.moov.traks || []).find(function(t) {
                    return t && t.mdia && t.mdia.hdlr && t.mdia.hdlr.handler === 'vide';
                });
                if (!trakVid) { resolve(null); return; }
                var stbl = trakVid.mdia && trakVid.mdia.minf && trakVid.mdia.minf.stbl;
                var timescale = trakVid.mdia && trakVid.mdia.mdhd && trakVid.mdia.mdhd.timescale;
                if (!stbl || !timescale) { resolve(null); return; }
                var stts = stbl.stts;
                var stss = stbl.stss;

                /* Presentation times, not decode times. The stts walk below
                   yields DTS; a sync sample's PTS differs from it by the
                   composition offset minus the edit list, and on a B-frame
                   file muxed WITHOUT an edit list that is 2–3 frames. Every
                   consumer uses these times as PRESENTATION positions (a
                   trim start, an `-ss`), so a DTS here put the smart-trim
                   head 80 ms short of the copied keyframe on such a file.
                   The sample table also carries what a splice needs to know
                   about each keyframe (where its bytes are). */
                var viaSamples = presentationIndex(mp4, trakVid, timescale);
                if (viaSamples) {
                    var dur0 = info.timescale ? info.duration / info.timescale : 0;
                    var kf0 = viaSamples.keyframes;
                    resolve({
                        keyframes: kf0, source: 'mp4box', duration: dur0,
                        meanGopSec: kf0.length > 1 ? (kf0[kf0.length - 1] - kf0[0]) / (kf0.length - 1) : dur0,
                        frames: viaSamples.frames, splice: viaSamples.splice,
                    });
                    return;
                }

                /* Build per-sample decoding time by walking stts.
                   sample_counts[i] samples each have delta sample_deltas[i]. */
                var dts = 0;
                var dtsPerSample = [];
                if (stts && stts.sample_counts && stts.sample_deltas) {
                    for (var i = 0; i < stts.sample_counts.length; i++) {
                        var cnt = stts.sample_counts[i];
                        var delta = stts.sample_deltas[i];
                        for (var j = 0; j < cnt; j++) {
                            dtsPerSample.push(dts);
                            dts += delta;
                        }
                    }
                }
                if (dtsPerSample.length === 0) { resolve(null); return; }

                var keyframes;
                if (stss && stss.sample_numbers && stss.sample_numbers.length > 0) {
                    /* File has a sync-sample table — keyframes are the listed
                       sample indices (1-based per ISO spec). */
                    keyframes = [];
                    var nums = stss.sample_numbers;
                    for (var k = 0; k < nums.length; k++) {
                        var idx0 = nums[k] - 1;
                        var t = dtsPerSample[idx0];
                        if (t != null) keyframes.push(t / timescale);
                    }
                } else {
                    /* No stss = every sample is a sync sample (intra-only:
                       MJPEG, ProRes, DNxHD, etc.). Treat each sample time
                       as a keyframe. Cap at 2000 to avoid pathological cases. */
                    keyframes = [];
                    var cap = Math.min(dtsPerSample.length, 2000);
                    for (var s = 0; s < cap; s++) keyframes.push(dtsPerSample[s] / timescale);
                }
                keyframes.sort(function(a, b){ return a - b; });
                var duration = info.timescale ? info.duration / info.timescale : 0;
                var meanGop = keyframes.length > 1
                    ? (keyframes[keyframes.length - 1] - keyframes[0]) / (keyframes.length - 1)
                    : duration;
                resolve({ keyframes: keyframes, source: 'mp4box', duration: duration, meanGopSec: meanGop });
            } catch (e) {
                resolve(null);
            }
        };
        mp4.onError = function() {
            if (settled) return; settled = true; clearTimeout(timer);
            resolve(null);
        };

        /* Feed the file in 4 MB chunks, honour mp4box's seek requests up
           to a 64 MB read budget — matches the bytes-read budget already in
           mp4box-probe.js so we accept moov-at-end files (mp4box-probe.js's
           BYTES_READ_BUDGET = 64 MB after the 2026-05-07 fix). */
        var offset = 0;
        var bytesRead = 0;
        var BUDGET = 64 * 1024 * 1024;
        var CHUNK = 4 * 1024 * 1024;

        (async function feed() {
            try {
                while (!settled && offset < file.size) {
                    var end = Math.min(offset + CHUNK, file.size);
                    var slice = file.slice(offset, end);
                    var buf = await slice.arrayBuffer();
                    bytesRead += buf.byteLength;
                    if (bytesRead > BUDGET) {
                        if (settled) return;
                        settled = true;
                        clearTimeout(timer);
                        resolve(null);
                        return;
                    }
                    buf.fileStart = offset;
                    var next = mp4.appendBuffer(buf);
                    if (settled) return;
                    offset = next > offset ? next : Math.max(end, next);
                }
                if (!settled) {
                    try { mp4.flush(); } catch(_) {}
                    if (!settled) {
                        settled = true;
                        clearTimeout(timer);
                        resolve(null);
                    }
                }
            } catch (e) {
                if (settled) return;
                settled = true;
                clearTimeout(timer);
                resolve(null);
            }
        })();
    });
}

/* ---- bitrate-over-time + GOP timeline (mp4-family, no decode) ------------ */

/* Walk the sample tables (stts time-to-sample, stsz sample-sizes, stss sync
   samples) of the video track to build a per-second bitrate series + GOP /
   keyframe distribution + I-vs-inter frame counts — the "bitrate viewer"
   feature, instant and decode-free. mp4-family only; non-mp4 returns null
   (the graph section degrades gracefully). P-vs-B is NOT distinguishable from
   the sample table (stss only marks I/sync); we honestly report I vs inter. */
window.__probeBitrateTimeline = async function(file) {
    if (!file || !file.size) return null;
    if (typeof window.__Mp4BoxProbe !== 'function' || !window.MP4Box) return null;
    if (!MP4_FAMILY_RE.test(file.name || '') && !MP4_MIME_RE.test(file.type || '')) return null;

    return await new Promise(function(resolve) {
        var mp4 = window.MP4Box.createFile();
        var settled = false;
        var timer = setTimeout(function() {
            if (settled) return; settled = true;
            try { mp4.flush(); } catch(_) {}
            resolve(null);
        }, 12000);

        mp4.onReady = function(info) {
            if (settled) return; settled = true; clearTimeout(timer);
            try {
                var trakVid = (mp4.moov && mp4.moov.traks || []).find(function(t) {
                    return t && t.mdia && t.mdia.hdlr && t.mdia.hdlr.handler === 'vide';
                });
                if (!trakVid) { resolve(null); return; }
                var stbl = trakVid.mdia && trakVid.mdia.minf && trakVid.mdia.minf.stbl;
                var timescale = trakVid.mdia && trakVid.mdia.mdhd && trakVid.mdia.mdhd.timescale;
                if (!stbl || !timescale) { resolve(null); return; }
                var stts = stbl.stts, stsz = stbl.stsz, stss = stbl.stss;
                if (!stts || !stts.sample_counts || !stts.sample_deltas || !stsz) { resolve(null); return; }

                /* Per-sample decoding time (seconds). */
                var dtsPerSample = [];
                var dts = 0;
                for (var i = 0; i < stts.sample_counts.length; i++) {
                    var cnt = stts.sample_counts[i], delta = stts.sample_deltas[i];
                    for (var j = 0; j < cnt; j++) { dtsPerSample.push(dts / timescale); dts += delta; }
                }
                var n = dtsPerSample.length;
                if (n === 0) { resolve(null); return; }

                /* Per-sample size (bytes). stsz: uniform sample_size>0, else sample_sizes[]. */
                function sizeAt(idx) {
                    if (stsz.sample_size && stsz.sample_size > 0) return stsz.sample_size;
                    return (stsz.sample_sizes && stsz.sample_sizes[idx]) || 0;
                }

                var duration = info.timescale ? info.duration / info.timescale : (dtsPerSample[n - 1] || 0);
                var nBuckets = Math.max(1, Math.ceil(duration || (dtsPerSample[n - 1] + 0.001)));
                if (nBuckets > 200000) { resolve(null); return; }   // pathological — bail to lite
                var bucketBits = new Float64Array(nBuckets);
                var totalBytes = 0;
                for (var s = 0; s < n; s++) {
                    var sz = sizeAt(s);
                    totalBytes += sz;
                    var b = Math.min(nBuckets - 1, Math.floor(dtsPerSample[s]));
                    bucketBits[b] += sz * 8;
                }
                var perSecondKbps = new Array(nBuckets);
                var peakKbps = 0;
                for (var b2 = 0; b2 < nBuckets; b2++) {
                    var kb = bucketBits[b2] / 1000;
                    perSecondKbps[b2] = kb;
                    if (kb > peakKbps) peakKbps = kb;
                }
                var avgKbps = duration > 0 ? (totalBytes * 8 / 1000) / duration : 0;
                var fps = duration > 0 ? n / duration : 0;

                /* Keyframes (sync samples) → seconds + sample-index gaps for GOP. */
                var kfTimes = [], kfIdx = [];
                if (stss && stss.sample_numbers && stss.sample_numbers.length > 0) {
                    var nums = stss.sample_numbers;
                    for (var k = 0; k < nums.length; k++) {
                        var idx0 = nums[k] - 1;
                        if (dtsPerSample[idx0] != null) { kfTimes.push(dtsPerSample[idx0]); kfIdx.push(idx0); }
                    }
                } else {
                    /* No stss → intra-only (ProRes/MJPEG: every frame is a keyframe).
                       Cap the retained list so a multi-hour file doesn't pin a
                       huge array on report.video.timeline.keyframes. */
                    var icap = Math.min(n, 2000);
                    for (var a = 0; a < icap; a++) { kfTimes.push(dtsPerSample[a]); kfIdx.push(a); }
                }
                var iCount = (stss && stss.sample_numbers) ? stss.sample_numbers.length : n;
                var interCount = n - iCount;

                /* GOP length in frames between consecutive keyframes. */
                var gopMin = Infinity, gopMax = 0, gopSum = 0, gopCnt = 0;
                for (var gi = 1; gi < kfIdx.length; gi++) {
                    var gap = kfIdx[gi] - kfIdx[gi - 1];
                    if (gap > 0) { gopSum += gap; gopCnt++; if (gap < gopMin) gopMin = gap; if (gap > gopMax) gopMax = gap; }
                }
                var gopMeanFrames = gopCnt > 0 ? gopSum / gopCnt : n;
                var meanGopSec = kfTimes.length > 1 ? (kfTimes[kfTimes.length - 1] - kfTimes[0]) / (kfTimes.length - 1) : duration;

                resolve({
                    source: 'mp4box',
                    duration: duration,
                    fps: fps,
                    sampleCount: n,
                    perSecondKbps: perSecondKbps,
                    avgKbps: avgKbps,
                    peakKbps: peakKbps,
                    keyframes: kfTimes,
                    frameTypes: { I: iCount, inter: interCount, total: n },
                    gop: {
                        meanFrames: gopMeanFrames,
                        minFrames: isFinite(gopMin) ? gopMin : gopMeanFrames,
                        maxFrames: gopMax || gopMeanFrames,
                        meanSec: meanGopSec,
                        count: kfTimes.length,
                    },
                });
            } catch (e) { resolve(null); }
        };
        mp4.onError = function() { if (settled) return; settled = true; clearTimeout(timer); resolve(null); };

        var offset = 0, bytesRead = 0, BUDGET = 96 * 1024 * 1024, CHUNK = 4 * 1024 * 1024;
        (async function feed() {
            try {
                while (!settled && offset < file.size) {
                    var end = Math.min(offset + CHUNK, file.size);
                    var buf = await file.slice(offset, end).arrayBuffer();
                    bytesRead += buf.byteLength;
                    if (bytesRead > BUDGET) { if (!settled){ settled = true; clearTimeout(timer); resolve(null);} return; }
                    buf.fileStart = offset;
                    var next = mp4.appendBuffer(buf);
                    if (settled) return;
                    offset = next > offset ? next : Math.max(end, next);
                }
                if (!settled) {
                    try { mp4.flush(); } catch(_) {}
                    if (!settled) { settled = true; clearTimeout(timer); resolve(null); }
                }
            } catch (e) { if (!settled){ settled = true; clearTimeout(timer); resolve(null); } }
        })();
    });
};

/* ---- FFmpeg fallback tier ----------------------------------------------- */

/* For non-mp4 containers (AVI, MKV, TS, WMV, FLV, OGV, WebM) — universal
   fallback. Runs `-skip_frame nokey -i input -vf showinfo -fps_mode
   passthrough -f null -` which demux-skips non-keyframes (cheap, no full
   decode) and writes one showinfo log line per I-frame.
   Cost: ~150 ms on 20 s @ 30 fps, ~1-3 s on a 30 min recording. */
async function probeViaFFmpeg(file) {
    if (!window.FFmpegProcessor || typeof window.FFmpegProcessor.probeKeyframesNative !== 'function') return null;
    try {
        var result = await window.FFmpegProcessor.probeKeyframesNative(file);
        if (!result || !Array.isArray(result.keyframes) || result.keyframes.length === 0) return null;
        var meanGop = result.keyframes.length > 1
            ? (result.keyframes[result.keyframes.length - 1] - result.keyframes[0]) / (result.keyframes.length - 1)
            : (result.duration || 0);
        return { keyframes: result.keyframes, source: 'ffmpeg', duration: result.duration || 0, meanGopSec: meanGop };
    } catch (_) { return null; }
}

/* ---- public API --------------------------------------------------------- */

window.__probeKeyframes = async function(file) {
    if (!file || !file.size) return null;
    if (_cache.has(file)) return _cache.get(file);

    var t0 = (typeof performance !== 'undefined') ? performance.now() : Date.now();

    /* Tier 1: mp4box. Try first for mp4-family files. Instant when it works. */
    var res = await probeViaMp4Box(file);

    /* Tier 2: FFmpeg fallback. Only invoked if mp4box couldn't resolve. */
    if (!res) res = await probeViaFFmpeg(file);

    if (res) {
        res.durationMs = Math.round(((typeof performance !== 'undefined') ? performance.now() : Date.now()) - t0);
        _cache.set(file, res);
    }
    return res;
};

/* Binary-search helpers — keyframes are sorted ascending */
window.__keyframeHelpers = {
    nearestKeyframeBefore: function(kfs, t) {
        if (!kfs || kfs.length === 0) return 0;
        if (t <= kfs[0]) return kfs[0];
        var lo = 0, hi = kfs.length - 1;
        while (lo < hi) {
            var mid = (lo + hi + 1) >>> 1;
            if (kfs[mid] <= t) lo = mid; else hi = mid - 1;
        }
        return kfs[lo];
    },
    nearestKeyframeAfter: function(kfs, t) {
        if (!kfs || kfs.length === 0) return t;
        if (t >= kfs[kfs.length - 1]) return kfs[kfs.length - 1];
        var lo = 0, hi = kfs.length - 1;
        while (lo < hi) {
            var mid = (lo + hi) >>> 1;
            if (kfs[mid] >= t) hi = mid; else lo = mid + 1;
        }
        return kfs[lo];
    },
    nearestKeyframe: function(kfs, t) {
        var before = window.__keyframeHelpers.nearestKeyframeBefore(kfs, t);
        var after = window.__keyframeHelpers.nearestKeyframeAfter(kfs, t);
        return (t - before <= after - t) ? before : after;
    },
    isOnKeyframe: function(kfs, t, tolSec) {
        if (!kfs || kfs.length === 0) return false;
        var nearest = window.__keyframeHelpers.nearestKeyframe(kfs, t);
        return Math.abs(t - nearest) <= (tolSec == null ? 0.05 : tolSec);
    },
    /* Start of the frame on screen at `t` (largest frame PTS ≤ t). A trim
       starting between two frames shows the earlier one from output 0, so the
       audio must start there too — cutting audio at `t` itself put it
       (t − frameStart) behind the picture, up to one frame. Returns `t`
       unchanged when the probe has no frame table (FFmpeg tier). */
    frameStartAt: function(info, t) {
        var f = info && info.frames;
        if (!f || !f.length || !(t >= f[0])) return t;
        var lo = 0, hi = f.length - 1;
        while (lo < hi) {
            var mid = (lo + hi + 1) >>> 1;
            if (f[mid] <= t + 1e-6) lo = mid; else hi = mid - 1;
        }
        return f[lo];
    },
};

/* First keyframe at or after `t` (within `maxAheadSec`) where a copied tail
   may follow a re-encoded H.264 head — see spliceKeyframeAfter. Resolves
   { ok:true, t } or { ok:false, reason }. */
window.__spliceKeyframeAfter = spliceKeyframeAfter;

})();

/* Drain stub queue if installed by timbrica-core */
if (typeof window.__drainSharedFunction === 'function') window.__drainSharedFunction('__probeKeyframes');
